import React, { useState } from 'react';
import { 
  Power, 
  Clock, 
  CheckCircle,
  XCircle
} from 'lucide-react';
import { ResponderAvailability } from '../types';
import { SocketManager } from '../utils/socketManager';

interface ResponderAvailabilityToggleProps {
  responderId: string;
  responderName: string;
  currentAvailability: ResponderAvailability;
  onAvailabilityChange: (availability: ResponderAvailability) => void;
  className?: string;
}

export const ResponderAvailabilityToggle: React.FC<ResponderAvailabilityToggleProps> = ({
  responderId,
  responderName,
  currentAvailability,
  onAvailabilityChange,
  className = ''
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [socketManager] = useState(() => new SocketManager());

  const availabilityOptions: { 
    value: ResponderAvailability; 
    label: string; 
    color: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
  }[] = [
    {
      value: 'available',
      label: 'Available',
      color: 'bg-green-600 hover:bg-green-700',
      icon: CheckCircle,
      description: 'Ready to respond to emergencies'
    },
    {
      value: 'busy',
      label: 'Busy',
      color: 'bg-yellow-600 hover:bg-yellow-700',
      icon: Clock,
      description: 'Currently handling an emergency'
    },
    {
      value: 'offline',
      label: 'Offline',
      color: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      description: 'Not available for emergency response'
    }
  ];

  const handleAvailabilityChange = async (newAvailability: ResponderAvailability) => {
    if (newAvailability === currentAvailability) return;

    setIsUpdating(true);
    
    try {
      // Update via socket
      socketManager.updateResponderAvailability(responderId, newAvailability);
      
      // Update local state
      onAvailabilityChange(newAvailability);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (error) {
      console.error('Failed to update availability:', error);
      alert('Failed to update availability. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const getCurrentOption = () => {
    return availabilityOptions.find(option => option.value === currentAvailability);
  };

  const currentOption = getCurrentOption();
  const CurrentIcon = currentOption?.icon || Power;

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <Power className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">Availability Status</h3>
      </div>

      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          <strong>Responder:</strong> {responderName}
        </div>

        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <CurrentIcon className={`w-5 h-5 ${
            currentAvailability === 'available' ? 'text-green-600' :
            currentAvailability === 'busy' ? 'text-yellow-600' : 'text-red-600'
          }`} />
          <div>
            <div className="font-medium text-gray-900">
              Current Status: {currentOption?.label}
            </div>
            <div className="text-sm text-gray-600">
              {currentOption?.description}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Change Status:
          </label>
          <div className="grid grid-cols-1 gap-2">
            {availabilityOptions.map((option) => {
              const OptionIcon = option.icon;
              const isSelected = option.value === currentAvailability;
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleAvailabilityChange(option.value)}
                  disabled={isUpdating || isSelected}
                  className={`flex items-center gap-3 p-3 rounded-lg text-white font-medium transition-colors ${
                    isSelected 
                      ? `${option.color} opacity-75 cursor-default` 
                      : `${option.color} ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`
                  }`}
                >
                  <OptionIcon className="w-5 h-5" />
                  <div className="text-left">
                    <div>{option.label}</div>
                    <div className="text-sm opacity-90">{option.description}</div>
                  </div>
                  {isSelected && (
                    <CheckCircle className="w-5 h-5 ml-auto" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {isUpdating && (
          <div className="text-center text-sm text-gray-600">
            Updating availability status...
          </div>
        )}
      </div>
    </div>
  );
};